import { PARKING_PROPERTIES_FILTERS, type ParkingProperty, type ParkingPropertyFilter } from '@repo/domain';
import { useMemo } from 'react';

import { useParkingSearchStore } from '@shared/stores/useParkingSearchStore';

/**
 * The filter keys that also exist as a `ParkingProperty` and can therefore be
 * sent to the backend. `nonCompact` is the only filter left out: it is the
 * inverse of the `compact` property, so it needs a dedicated backend
 * representation before it can be forwarded.
 */
type ForwardableParkingFilter = ParkingPropertyFilter & ParkingProperty;

const isForwardableFilter = (filter: ParkingPropertyFilter): filter is ForwardableParkingFilter =>
  filter !== 'nonCompact';

/**
 * The properties the user selected in the global search filter, in the shape the
 * price-estimate and reservation-create endpoints expect. Returns `undefined`
 * when nothing is selected so the field stays absent from the request.
 */
export function useSelectedParkingProperties(): ParkingProperty[] | undefined {
  const filters = useParkingSearchStore((state) => state.filters);

  // Keyed on the forwardable selection alone, not on the whole `filters` object.
  // Toggling a filter that is never sent (`nonCompact`) must not produce a new
  // array identity: price queries run with `staleTime: 0`, so a changed identity
  // would churn the query key and refetch every parking row for nothing.
  const selectedKey = PARKING_PROPERTIES_FILTERS.filter(
    (filter): filter is ForwardableParkingFilter => filters[filter] && isForwardableFilter(filter)
  ).join(',');

  return useMemo<ParkingProperty[] | undefined>(() => {
    if (selectedKey === '') return undefined;

    return selectedKey.split(',') as ForwardableParkingFilter[];
  }, [selectedKey]);
}
